import { useMemo, useState, useEffect } from "react";
import axios from "axios";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { getApiBase } from "../utils/config";
import "./SearchVehicle.css";

const API_BASE = getApiBase();

const NON_EDITABLE_KEYS = ["id", "Registration_No", "created_at", "updated_at", "_srNo"];

function DynamicTable({ columns, rows, onDelete, onEdit, sectionKey, onBulkDelete }) {
  const [selectedIds, setSelectedIds] = useState(new Set());
  if (!rows || rows.length === 0) return null;
  const hasDelete = typeof onDelete === "function";
  const hasEdit = typeof onEdit === "function" && sectionKey;
  const hasBulkDelete = typeof onBulkDelete === "function" && sectionKey;

  const validRows = rows.filter((r) => {
    const id = r?.id;
    return id !== undefined && id !== null && id !== "" && !isNaN(Number(id)) && Number(id) > 0;
  });
  const allValidIds = validRows.map((r) => Number(r.id));
  const isAllSelected = allValidIds.length > 0 && allValidIds.every((id) => selectedIds.has(id));

  const toggleRow = (rowId) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(rowId)) next.delete(rowId);
      else next.add(rowId);
      return next;
    });
  };
  const toggleAll = () => {
    if (isAllSelected) setSelectedIds(new Set());
    else setSelectedIds(new Set(allValidIds));
  };
  const handleBulkDelete = () => {
    if (selectedIds.size === 0) return;
    onBulkDelete(Array.from(selectedIds));
    setSelectedIds(new Set());
  };

  return (
    <div className="sv-section">
      {hasBulkDelete && selectedIds.size > 0 && (
        <div className="sv-bulk-actions">
          <span className="sv-bulk-count">{selectedIds.size} selected</span>
          <button type="button" className="sv-bulk-delete-btn" onClick={handleBulkDelete}>
            Bulk delete selected
          </button>
        </div>
      )}
      <div className="sv-table-container">
        <table className="sv-table">
          <thead>
            <tr>
              {hasBulkDelete && (
                <th style={{ width: 44 }}>
                  <input
                    type="checkbox"
                    checked={isAllSelected}
                    onChange={toggleAll}
                    title="Select all"
                  />
                </th>
              )}
              {columns.map((c) => (
                <th key={c.key}>{c.label}</th>
              ))}
              {(hasEdit || hasDelete) && <th style={{ width: hasEdit && hasDelete ? 100 : 56 }}>Actions</th>}
            </tr>
          </thead>
          <tbody>
            {rows.map((r, idx) => {
              const rowId = r?.id;
              const isValidId = rowId !== undefined && rowId !== null && rowId !== "" && !isNaN(rowId) && String(rowId).trim() !== "" && Number(rowId) > 0;

              return (
                <tr key={idx}>
                  {hasBulkDelete && (
                    <td style={{ width: 44, textAlign: "center" }}>
                      {isValidId ? (
                        <input
                          type="checkbox"
                          checked={selectedIds.has(Number(rowId))}
                          onChange={() => toggleRow(Number(rowId))}
                        />
                      ) : null}
                    </td>
                  )}
                  {columns.map((c) => (
                    <td key={c.key}>{r?.[c.key] ?? ""}</td>
                  ))}
                  {(hasEdit || hasDelete) && (
                    <td style={{ textAlign: "center", whiteSpace: "nowrap" }}>
                      {hasEdit && isValidId && (
                        <button
                          type="button"
                          className="sv-edit-btn"
                          onClick={() => onEdit(sectionKey, r)}
                          title={`Edit row (ID: ${rowId})`}
                        >
                          Edit
                        </button>
                      )}
                      {hasDelete && (
                        <button
                          className="sv-delete-btn"
                          onClick={() => isValidId ? onDelete(rowId) : undefined}
                          disabled={!isValidId}
                          title={isValidId ? `Delete row (ID: ${rowId})` : "Cannot delete: Missing ID"}
                        >
                          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                            <polyline points="3 6 5 6 21 6"></polyline>
                            <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>
                            <line x1="10" y1="11" x2="10" y2="17"></line>
                            <line x1="14" y1="11" x2="14" y2="17"></line>
                          </svg>
                        </button>
                      )}
                    </td>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function normalizeRegNo(value) {
  return String(value || "").trim().toUpperCase().replace(/\s+/g, "");
}

function getValue(row, keys) {
  if (!row || typeof row !== "object") return "";
  for (const key of keys) {
    const value = row[key];
    if (value !== undefined && value !== null && String(value).trim() !== "") {
      return String(value).trim();
    }
  }
  return "";
}

export default function SearchVehicle({ onGoUpload, notify }) {
  const [reg, setReg] = useState("");
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [errorMsg, setErrorMsg] = useState("");
  const [registrationList, setRegistrationList] = useState([]);
  const [loadingList, setLoadingList] = useState(false);
  const [showList, setShowList] = useState(true);
  const [editModal, setEditModal] = useState(null); // { sectionKey, row, sectionTitle }
  const [editForm, setEditForm] = useState({});
  const [editModalLoading, setEditModalLoading] = useState(false);

  // Fetch all registration numbers on component mount
  useEffect(() => {
    const fetchRegistrationList = async () => {
      setLoadingList(true);
      try {
        const token = localStorage.getItem("token") || "";
        const res = await axios.get(`${API_BASE}/vehicle/all-registrations`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (res.data?.status === "success" && Array.isArray(res.data.data)) {
          setRegistrationList(res.data.data);
        }
      } catch (e) {
        console.error("Failed to fetch registration list:", e);
        // Don't show error to user, just log it
      } finally {
        setLoadingList(false);
      }
    };
    fetchRegistrationList();
  }, []);

  const handleRegistrationClick = (registrationNo) => {
    setReg(registrationNo);
    setShowList(false);
    // Auto-search when clicked - pass registrationNo directly to avoid state timing issues
    performSearch(registrationNo);
  };

  const performSearch = async (registrationNumber = null) => {
    // Use provided registrationNumber or fall back to state
    const regToSearch = registrationNumber || reg;
    
    if (!regToSearch || !regToSearch.trim()) {
      notify?.("Vehicle Registration Number is required", "warning");
      return;
    }
    
    setLoading(true);
    setErrorMsg("");
    setShowList(false);
    
    // Update state if we're using a different registration number
    if (registrationNumber && registrationNumber !== reg) {
      setReg(registrationNumber);
    }
    
    try {
      const token = localStorage.getItem("token") || "";
      const res = await axios.get(`${API_BASE}/vehicle/${encodeURIComponent(regToSearch.trim())}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(res.data);
      notify?.("Vehicle history found", "success");
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.message || e.message || "Search failed";
      setErrorMsg(msg);
      setResult(null);
      notify?.(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  const search = () => {
    performSearch();
  };

  const deleteRow = async (sectionKey, id) => {
    const numericId = Number(id);
    if (String(id).trim() === '' || isNaN(id) || numericId <= 0) {
      notify?.("Row id missing or invalid; cannot delete", "warning");
      return;
    }

    // Confirmation dialog
    const confirmed = window.confirm(`Are you sure you want to delete this row (ID: ${id})? This action cannot be undone.`);
    if (!confirmed) {
      return;
    }

    try {
      const token = localStorage.getItem("token") || "";
      await axios.delete(`${API_BASE}/vehicle/${sectionKey}/${id}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      notify?.("Row deleted successfully from database", "success");
      
      // Refresh the search to get updated data from database
      const currentReg = reg.trim();
      if (currentReg) {
        setLoading(true);
        try {
          const res = await axios.get(`${API_BASE}/vehicle/${encodeURIComponent(currentReg)}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setResult(res.data);
        } catch (refreshErr) {
          console.error("Refresh error:", refreshErr);
          // If refresh fails, still show success for delete
        } finally {
          setLoading(false);
        }
      }
    } catch (e) {
      console.error("Delete error:", e);
      const msg = e?.response?.data?.message || e.message || "Delete failed";
      notify?.(msg, "error");
    }
  };

  const deleteBulk = async (sectionKey, ids) => {
    if (!Array.isArray(ids) || ids.length === 0) {
      notify?.("No rows selected to delete", "warning");
      return;
    }
    const confirmed = window.confirm(`Are you sure you want to delete ${ids.length} row(s)? This action cannot be undone.`);
    if (!confirmed) return;

    try {
      const token = localStorage.getItem("token") || "";
      const res = await axios.post(
        `${API_BASE}/vehicle/bulk-delete`,
        { section: sectionKey, ids },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notify?.(res?.data?.message ?? `${ids.length} row(s) deleted successfully`, "success");
      const currentReg = reg.trim();
      if (currentReg) {
        setLoading(true);
        try {
          const getRes = await axios.get(`${API_BASE}/vehicle/${encodeURIComponent(currentReg)}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setResult(getRes.data);
        } catch (refreshErr) {
          console.error("Refresh error:", refreshErr);
        } finally {
          setLoading(false);
        }
      }
    } catch (e) {
      console.error("Bulk delete error:", e);
      const msg = e?.response?.data?.message || e.message || "Bulk delete failed";
      notify?.(msg, "error");
    }
  };

  const openEditModal = (sectionKey, row, columns, sectionTitle) => {
    const editableCols = (columns || []).filter((c) => !NON_EDITABLE_KEYS.includes(c.key));
    const initial = {};
    editableCols.forEach((c) => {
      initial[c.key] = row?.[c.key] != null ? String(row[c.key]) : "";
    });
    setEditModal({ sectionKey, row, columns: editableCols, sectionTitle });
    setEditForm(initial);
  };

  const closeEditModal = () => {
    setEditModal(null);
    setEditForm({});
  };

  const handleSaveEdit = async () => {
    if (!editModal?.sectionKey || !editModal?.row?.id) return;
    setEditModalLoading(true);
    try {
      const token = localStorage.getItem("token") || "";
      await axios.put(
        `${API_BASE}/vehicle/${editModal.sectionKey}/${editModal.row.id}`,
        editForm,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      notify?.("Row updated successfully", "success");
      closeEditModal();
      const currentReg = reg.trim();
      if (currentReg) {
        setLoading(true);
        try {
          const res = await axios.get(`${API_BASE}/vehicle/${encodeURIComponent(currentReg)}`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          setResult(res.data);
        } catch (refreshErr) {
          console.error("Refresh error:", refreshErr);
        } finally {
          setLoading(false);
        }
      }
    } catch (e) {
      console.error("Edit save error:", e);
      const msg = e?.response?.data?.message || e.message || "Update failed";
      notify?.(msg, "error");
    } finally {
      setEditModalLoading(false);
    }
  };

  const data = useMemo(() => result?.data || {}, [result]);
  const vehiclesFullData = useMemo(() => {
    const rows = data.vehicles_full_data || result?.vehicles_full_data || [];
    return Array.isArray(rows) ? rows : [];
  }, [data.vehicles_full_data, result?.vehicles_full_data]);
  const oldRegistrationNumbers = useMemo(() => {
    const currentReg = normalizeRegNo(result?.registration_no || reg);
    const normalizedRows = vehiclesFullData.map((row) => ({
      vehicleNo: getValue(row, ["vehicle_no", "registration_number", "Registration_No", "registration_no"]),
      oldRegistrationNo: getValue(row, ["old_registration_number", "oldRegistrationNumber", "Old Registration Number"]),
    }));
    const matchedRows = normalizedRows.filter((row) => {
      const vehicleNo = normalizeRegNo(row.vehicleNo);
      const oldRegistrationNo = normalizeRegNo(row.oldRegistrationNo);
      return currentReg && (vehicleNo === currentReg || oldRegistrationNo === currentReg);
    });
    const rowsToUse = matchedRows.length > 0 ? matchedRows : normalizedRows.length === 1 ? normalizedRows : [];
    return [
      ...new Set(
        rowsToUse
          .map((row) => row.oldRegistrationNo)
          .filter(Boolean)
      ),
    ];
  }, [vehiclesFullData, result?.registration_no, reg]);
  const hasAnyData = useMemo(() => {
    const lists = [
      data.challan_history,
      data.hypothecation_history,
      data.insurance_history,
      data.paid_fees_history,
      data.road_tax_history,
      data.ownership_history,
      data.blacklist_history,
      data.permit_history,
      data.tax_clear_history,
      data.fitness_history,
      data.changed_vehicle_history_by_admins,
      data.rc_print_history,
      data.noc_history,
      data.vehicles_full_data,
    ];
    return lists.some((arr) => Array.isArray(arr) && arr.length > 0);
  }, [data]);

  // Columns (match your database schema)
  const challanCols = useMemo(
    () => [
      { key: "id", label: "ID" },
      { key: "Registration_No", label: "REGISTRATION NO" },
      { key: "Serial_No", label: "SERIAL NO" },
      { key: "Challan_No", label: "CHALLAN NO" },
      { key: "Offense_Details", label: "OFFENSE DETAILS" },
      { key: "Challan_Place", label: "CHALLAN PLACE" },
      { key: "Challan_Date_Time", label: "CHALLAN DATE TIME" },
      { key: "State", label: "STATE" },
      { key: "RTO", label: "RTO" },
      { key: "Accused_Name", label: "ACCUSED NAME" },
      { key: "Amount", label: "AMOUNT" },
      { key: "Challan_Status", label: "CHALLAN STATUS" },
      { key: "Payment_Source", label: "PAYMENT SOURCE" },
      { key: "Payment_Date", label: "PAYMENT DATE" },
      { key: "Receipt_Number", label: "RECEIPT NUMBER" },
      { key: "sent_to_reg_court", label: "SENT TO REG COURT" },
      { key: "remark", label: "REMARK" },
      { key: "dl_no", label: "DL NO" },
      { key: "driver_name", label: "DRIVER NAME" },
      { key: "owner_name", label: "OWNER NAME" },
      { key: "department", label: "DEPARTMENT" },
      { key: "document_impounded", label: "DOCUMENT IMPOUNDED" },
      { key: "amount_of_fine_imposed", label: "AMOUNT OF FINE IMPOSED" },
      { key: "court_address", label: "COURT ADDRESS" },
      { key: "court_name", label: "COURT NAME" },
      { key: "date_of_proceeding", label: "DATE OF PROCEEDING" },
      { key: "sent_to_court_on", label: "SENT TO COURT ON" },
      { key: "sent_to_virtual_court", label: "SENT TO VIRTUAL COURT" },
      { key: "rto_distric_name", label: "RTO DISTRICT NAME" },
      { key: "created_at", label: "CREATED AT" },
      { key: "updated_at", label: "UPDATED AT" },
    ],
    []
  );

  // Hypothecation columns: match backend schema history_of_hypothecations + Sr.No for display
  const hypoCols = useMemo(
    () => [
      { key: "_srNo", label: "SR NO" },
      { key: "id", label: "ID" },
      { key: "Registration_No", label: "REGISTRATION NO" },
      { key: "Application_No", label: "APPLICATION NO" },
      { key: "Hypothecation_Type", label: "HYPOTHECATION TYPE" },
      { key: "Financer_Name", label: "FINANCER NAME" },
      { key: "Financer_Address", label: "FINANCER ADDRESS" },
      { key: "From_Date", label: "FROM DATE" },
      { key: "To_Date", label: "TO DATE" },
      { key: "Termination_Date", label: "TERMINATION DATE" },
      { key: "Office", label: "OFFICE" },
      { key: "created_at", label: "CREATED AT" },
      { key: "updated_at", label: "UPDATED AT" },
    ],
    []
  );

  // Normalize hypothecation rows to schema keys (backend sends normalized; this handles casing) and add Sr.No
  const hypothecationRows = useMemo(() => {
    const raw = data.hypothecation_history;
    if (!Array.isArray(raw)) return [];
    const schemaKeys = ["id", "Registration_No", "Application_No", "Hypothecation_Type", "Financer_Name", "Financer_Address", "From_Date", "To_Date", "Termination_Date", "Office", "created_at", "updated_at"];
    const get = (row, key) => {
      if (!row || typeof row !== "object") return null;
      const lower = key.toLowerCase();
      const camel = key.replace(/_([a-z])/gi, (_, c) => c.toUpperCase());
      return row[key] ?? row[lower] ?? row[camel] ?? null;
    };
    return raw.map((r, idx) => {
      const row = {};
      schemaKeys.forEach((k) => { row[k] = get(r, k); });
      row._srNo = idx + 1;
      return row;
    });
  }, [data.hypothecation_history]);

  const insCols = useMemo(
    () => [
      { key: "id", label: "ID" },
      { key: "Registration_No", label: "REGISTRATION NO" },
      { key: "Sr_No", label: "SR NO" },
      { key: "Insurance_Company", label: "INSURANCE COMPANY" },
      { key: "Insurance_Type", label: "INSURANCE TYPE" },
      { key: "Insurance_From", label: "INSURANCE FROM" },
      { key: "Insurance_Upto", label: "INSURANCE UPTO" },
      { key: "Cover_Note_No", label: "COVER NOTE NO" },
      { key: "Office", label: "OFFICE" },
      { key: "created_at", label: "CREATED AT" },
      { key: "updated_at", label: "UPDATED AT" },
    ],
    []
  );

  const paidFeesCols = useMemo(
    () => [
      { key: "id", label: "ID" },
      { key: "Registration_No", label: "REGISTRATION NO" },
      { key: "Receipt_No", label: "RECEIPT NO" },
      { key: "Receipt_Date", label: "RECEIPT DATE" },
      { key: "Fee_Amount", label: "FEE AMOUNT" },
      { key: "Fine", label: "FINE" },
      { key: "Fee_Particular", label: "FEE PARTICULAR" },
      { key: "Office", label: "OFFICE" },
      { key: "GRN_No", label: "GRN NO" },
      { key: "Fee_Collected_As", label: "FEE COLLECTED AS" },
      { key: "created_at", label: "CREATED AT" },
      { key: "updated_at", label: "UPDATED AT" },
    ],
    []
  );

  const roadTaxCols = useMemo(
    () => [
      { key: "id", label: "ID" },
      { key: "Registration_No", label: "REGISTRATION NO" },
      { key: "Receipt_No", label: "RECEIPT NO" },
      { key: "Tax_From", label: "TAX FROM" },
      { key: "Tax_Upto", label: "TAX UPTO" },
      { key: "Tax_Type", label: "TAX TYPE" },
      { key: "Challan_Date", label: "CHALLAN DATE" },
      { key: "Total_Amount", label: "TOTAL AMOUNT" },
      { key: "Office", label: "OFFICE" },
      { key: "Tax", label: "TAX" },
      { key: "Penalty", label: "PENALTY" },
      { key: "GRN_No", label: "GRN NO" },
      { key: "Breakup", label: "BREAKUP" },
      { key: "Tax_Mode", label: "TAX MODE" },
      { key: "created_at", label: "CREATED AT" },
      { key: "updated_at", label: "UPDATED AT" },
    ],
    []
  );

  const ownershipCols = useMemo(
    () => [
      { key: "id", label: "ID" },
      { key: "Registration_No", label: "REGISTRATION NO" },
      { key: "Application_No", label: "APPLICATION NO" },
      { key: "Ownership_Serial", label: "OWNERSHIP SERIAL" },
      { key: "Owner_From", label: "OWNER FROM" },
      { key: "Owner_Upto", label: "OWNER UPTO" },
      { key: "Owner_Name", label: "OWNER NAME" },
      { key: "Father_Husband_Name", label: "FATHER/HUSBAND NAME" },
      { key: "Present_Address", label: "PRESENT ADDRESS" },
      { key: "Permanent_Address", label: "PERMANENT ADDRESS" },
      { key: "Owner_Type", label: "OWNER TYPE" },
      { key: "Sale_Auction_Date", label: "SALE/AUCTION DATE" },
      { key: "Sale_Amount", label: "SALE AMOUNT" },
      { key: "Reason", label: "REASON" },
      { key: "Office", label: "OFFICE" },
      { key: "created_at", label: "CREATED AT" },
      { key: "updated_at", label: "UPDATED AT" },
    ],
    []
  );

  const blacklistCols = useMemo(
    () => [
      { key: "id", label: "ID" },
      { key: "Registration_No", label: "REGISTRATION NO" },
      { key: "Complain_File_Number", label: "COMPLAIN FILE NUMBER" },
      { key: "Complain_Date", label: "COMPLAIN DATE" },
      { key: "Complain_Entered_By", label: "COMPLAIN ENTERED BY" },
      { key: "Action_Taken", label: "ACTION TAKEN" },
      { key: "Action_Entered_By", label: "ACTION ENTERED BY" },
      { key: "Action_Date", label: "ACTION DATE" },
      { key: "Office", label: "OFFICE" },
      { key: "Compounding_Amount", label: "COMPOUNDING AMOUNT" },
      { key: "created_at", label: "CREATED AT" },
      { key: "updated_at", label: "UPDATED AT" },
    ],
    []
  );

  const permitCols = useMemo(
    () => [
      { key: "id", label: "ID" },
      { key: "Registration_No", label: "REGISTRATION NO" },
      { key: "Application_No", label: "APPLICATION NO" },
      { key: "Permit_No", label: "PERMIT NO" },
      { key: "Issue_Date", label: "ISSUE DATE" },
      { key: "Valid_From", label: "VALID FROM" },
      { key: "Valid_Upto", label: "VALID UPTO" },
      { key: "Permit_Type", label: "PERMIT TYPE" },
      { key: "Permit_Category", label: "PERMIT CATEGORY" },
      { key: "Office", label: "OFFICE" },
      { key: "created_at", label: "CREATED AT" },
      { key: "updated_at", label: "UPDATED AT" },
    ],
    []
  );

  const taxClearCols = useMemo(
    () => [
      { key: "id", label: "ID" },
      { key: "Registration_No", label: "REGISTRATION NO" },
      { key: "Sr_No", label: "SR NO" },
      { key: "Tax_Description", label: "TAX DESCRIPTION" },
      { key: "Tax_Clear_To", label: "TAX CLEAR TO" },
      { key: "TCR_No", label: "TCR NO" },
      { key: "Operation_Date", label: "OPERATION DATE" },
      { key: "Remarks", label: "REMARKS" },
      { key: "Office", label: "OFFICE" },
      { key: "created_at", label: "CREATED AT" },
      { key: "updated_at", label: "UPDATED AT" },
    ],
    []
  );

  const fitnessCols = useMemo(
    () => [
      { key: "id", label: "ID" },
      { key: "Registration_No", label: "REGISTRATION NO" },
      { key: "Appl_No", label: "APPLICATION NO" },
      { key: "Fitness_Check_Date", label: "FITNESS CHECK DATE" },
      { key: "Result", label: "RESULT" },
      { key: "Fitness_UPTO", label: "FITNESS UPTO" },
      { key: "NID", label: "NID" },
      { key: "Operation_Date", label: "OPERATION DATE" },
      { key: "Fitness_Officer_Name1", label: "OFFICER NAME 1" },
      { key: "Fitness_Officer_Name2", label: "OFFICER NAME 2" },
      { key: "Office", label: "OFFICE" },
      { key: "Remark", label: "REMARK" },
      { key: "created_at", label: "CREATED AT" },
    ],
    []
  );

  // Format date for display (ISO or YYYY-MM-DD -> DD-MMM-YYYY)
  const formatDateDisplay = (val) => {
    if (val == null || val === "") return "";
    const s = String(val).trim();
    if (!s) return "";
    const d = new Date(s);
    if (isNaN(d)) return s;
    const day = d.getDate();
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    const month = months[d.getMonth()];
    const year = d.getFullYear();
    return `${String(day).padStart(2, "0")}-${month}-${year}`;
  };

  // Normalize fitness rows so all columns show regardless of API key casing; format dates for display
  const fitnessHistoryDisplay = useMemo(() => {
    const raw = data.fitness_history;
    if (!Array.isArray(raw)) return [];
    const get = (r, keys) => {
      for (const k of keys) {
        const v = r?.[k];
        if (v !== undefined && v !== null && v !== "") return v;
      }
      return null;
    };
    return raw.map((r) => {
      const fitnessCheckDate = get(r, ["Fitness_Check_Date", "Fitness_Check_date", "FitnessCheckDate", "fitness_check_date"]);
      const fitnessUpto = get(r, ["Fitness_UPTO", "Fitness_Upto", "FitnessUpto", "fitness_upto"]);
      const operationDate = get(r, ["Operation_Date", "OperationDate", "operation_date"]);
      const createdAt = r?.created_at;
      return {
        id: r?.id,
        Registration_No: r?.Registration_No ?? r?.registration_no ?? "",
        Appl_No: get(r, ["Appl_No", "ApplNo", "appl_no", "Application_No", "application_no"]) ?? "",
        Fitness_Check_Date: fitnessCheckDate != null ? formatDateDisplay(fitnessCheckDate) : "",
        Result: get(r, ["Result", "result"]) ?? "",
        Fitness_UPTO: fitnessUpto != null ? formatDateDisplay(fitnessUpto) : "",
        NID: get(r, ["NID", "nid"]) ?? "",
        Operation_Date: operationDate != null ? formatDateDisplay(operationDate) : "",
        Fitness_Officer_Name1: get(r, ["Fitness_Officer_Name1", "FitnessOfficerName1", "fitness_officer_name1"]) ?? "",
        Fitness_Officer_Name2: get(r, ["Fitness_Officer_Name2", "FitnessOfficerName2", "fitness_officer_name2"]) ?? "",
        Office: get(r, ["Office", "office"]) ?? "",
        Remark: get(r, ["Remark", "remark"]) ?? "",
        created_at: createdAt != null ? formatDateDisplay(createdAt) : "",
      };
    });
  }, [data.fitness_history]);

  const changedCols = useMemo(
    () => [
      { key: "id", label: "ID" },
      { key: "Registration_No", label: "REGISTRATION NO" },
      { key: "Sr_No", label: "SR NO" },
      { key: "Changed_By", label: "CHANGED BY" },
      { key: "Changed_Data", label: "CHANGED DATA" },
      { key: "Changed_On", label: "CHANGED ON" },
      { key: "Office", label: "OFFICE" },
      { key: "created_at", label: "CREATED AT" },
    ],
    []
  );

  const rcCols = useMemo(
    () => [
      { key: "id", label: "ID" },
      { key: "Registration_No", label: "REGISTRATION NO" },
      { key: "Application_No", label: "APPLICATION NO" },
      { key: "Purpose", label: "PURPOSE" },
      { key: "Printed_On", label: "PRINTED ON" },
      { key: "Printed_By", label: "PRINTED BY" },
      { key: "created_at", label: "CREATED AT" },
      { key: "updated_at", label: "UPDATED AT" },
    ],
    []
  );

  const nocCols = useMemo(
    () => [
      { key: "id", label: "ID" },
      { key: "Registration_No", label: "REGISTRATION NO" },
      { key: "Application_No", label: "APPLICATION NO" },
      { key: "NOC_No", label: "NOC NO" },
      { key: "NOC_Date", label: "NOC DATE" },
      { key: "State_To", label: "STATE TO" },
      { key: "Office_To", label: "OFFICE TO" },
      { key: "Dispatch_No", label: "DISPATCH NO" },
      { key: "New_Owner", label: "NEW OWNER" },
      { key: "Office", label: "OFFICE" },
      { key: "created_at", label: "CREATED AT" },
      { key: "updated_at", label: "UPDATED AT" },
    ],
    []
  );

  const downloadPdf = () => {
    const doc = new jsPDF();
    doc.setFontSize(16);
    doc.text("VEHICLE HISTORY REPORT", 14, 15);
    doc.setFontSize(10);
    doc.text(`Registration No: ${result.registration_no}`, 14, 22);
    if (oldRegistrationNumbers.length > 0) {
      doc.text(`Old Registration No: ${oldRegistrationNumbers.join(", ")}`, 14, 28);
    }
    doc.text(`Generated on: ${new Date().toLocaleString()}`, 14, oldRegistrationNumbers.length > 0 ? 34 : 28);

    let currentY = oldRegistrationNumbers.length > 0 ? 41 : 35;

    const sections = [
      { title: "CHALLAN HISTORY", cols: challanCols, rows: data.challan_history },
      { title: "HYPOTHECATION HISTORY", cols: hypoCols, rows: data.hypothecation_history },
      { title: "INSURANCE HISTORY", cols: insCols, rows: data.insurance_history },
      { title: "PAID FEES HISTORY", cols: paidFeesCols, rows: data.paid_fees_history },
      { title: "ROAD TAX HISTORY", cols: roadTaxCols, rows: data.road_tax_history },
      { title: "OWNERSHIP HISTORY", cols: ownershipCols, rows: data.ownership_history },
      { title: "BLACKLIST HISTORY", cols: blacklistCols, rows: data.blacklist_history },
      { title: "PERMIT HISTORY", cols: permitCols, rows: data.permit_history },
      { title: "TAX CLEAR HISTORY", cols: taxClearCols, rows: data.tax_clear_history },
      { title: "FITNESS HISTORY", cols: fitnessCols, rows: fitnessHistoryDisplay },
      { title: "CHANGED VEHICLE HISTORY BY ADMIN", cols: changedCols, rows: data.changed_vehicle_history_by_admins },
      { title: "RC PRINT HISTORY", cols: rcCols, rows: data.rc_print_history },
      { title: "HISTORY OF NOC", cols: nocCols, rows: data.noc_history },
    ];

    sections.forEach((s) => {
      if (s.rows && s.rows.length > 0) {
        // Check if we need a new page before starting a section
        if (currentY > 250) {
          doc.addPage();
          currentY = 15;
        }

        doc.setFontSize(11);
        doc.setTextColor(40);
        doc.text(s.title, 14, currentY);

        autoTable(doc, {
          startY: currentY + 2,
          head: [s.cols.map((c) => c.label)],
          body: s.rows.map((r) => s.cols.map((c) => r?.[c.key] ?? "")),
          theme: "grid",
          styles: { fontSize: 7, cellPadding: 2 },
          headStyles: { fillColor: [23, 162, 184], textColor: 255 },
          margin: { left: 14, right: 14 },
        });

        currentY = doc.lastAutoTable.finalY + 12;
      }
    });

    doc.save(`Vehicle_History_${result.registration_no}.pdf`);
  };

  return (
    <div className="sv-page">
      <div className="sv-title">SEARCH VEHICLE</div>

      <div className="sv-card">
        <div className="sv-search-box">
          <div className="sv-label">
            Vehicle Registration Number<span>*</span>:
          </div>
          <input
            className="sv-input"
            value={reg}
            onChange={(e) => {
              setReg(e.target.value);
              // Show list when user types, but only if there are registration numbers
              if (registrationList.length > 0) {
                setShowList(true);
              }
            }}
            onFocus={() => {
              // Only show list if there are registration numbers
              if (registrationList.length > 0) {
                setShowList(true);
              }
            }}
            placeholder="e.g. RJ14AB1234, DL01CA9999"
          />
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", justifyContent: "center" }}>
            <button className="sv-btn" onClick={search} disabled={loading}>
              {loading ? "SEARCHING..." : "SEARCH"}
            </button>
            {onGoUpload && (
              <button className="sv-btn" onClick={onGoUpload} type="button">
                UPLOAD
              </button>
            )}
          </div>
        </div>

        {/* Registration Numbers List */}
        {showList && (
          <div className="sv-registration-list">
            <div className="sv-registration-list-header">
              <h3>Available Vehicle Registration Numbers {registrationList.length > 0 && `(${registrationList.length})`}</h3>
              <button 
                className="sv-registration-list-toggle" 
                onClick={() => setShowList(false)}
                title="Hide list"
              >
                ✕
              </button>
            </div>
            <div className="sv-registration-list-content">
              {loadingList ? (
                <div className="sv-registration-list-loading">Loading registration numbers...</div>
              ) : registrationList.length > 0 ? (
                <div className="sv-registration-list-items">
                  {registrationList.map((regNo, idx) => (
                    <button
                      key={idx}
                      className="sv-registration-item"
                      onClick={() => handleRegistrationClick(regNo)}
                      title={`Click to search: ${regNo}`}
                    >
                      {regNo}
                    </button>
                  ))}
                </div>
              ) : (
                <div className="sv-registration-list-empty">
                  No vehicle registration numbers found in database.
                </div>
              )}
            </div>
          </div>
        )}

        {result?.status === "success" && (
          <div className="sv-results">
            {!hasAnyData ? (
              <div className="sv-empty">
                <div className="sv-empty-title">No data found</div>
                <div className="sv-empty-sub">
                  No saved history exists for <b>{result.registration_no}</b>.
                </div>
                <div className="sv-empty-actions">
                  {onGoUpload && (
                    <button className="sv-btn" type="button" onClick={onGoUpload}>
                      AI Parser
                    </button>
                  )}
                  <button className="sv-btn sv-secondary" type="button" onClick={() => setResult(null)}>
                    New Search
                  </button>
                </div>
              </div>
            ) : (
              <>
                <div className="sv-section">
                  <div className="sv-section-header">
                    <div className="sv-section-title">Registration Information</div>
                  </div>
                  <table className="sv-table">
                    <tbody>
                      <tr>
                        <th>Registration Number</th>
                        <td>{result.registration_no}</td>
                      </tr>
                      {oldRegistrationNumbers.length > 0 && (
                        <tr>
                          <th>Old Registration Number</th>
                          <td>{oldRegistrationNumbers.join(", ")}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="sv-section">
                  <div className="sv-section-header">
                    <div className="sv-section-title">History Of Challan</div>
                  </div>
                  <DynamicTable columns={challanCols} rows={data.challan_history} onDelete={(id) => deleteRow("challan", id)} onEdit={(sk, row) => openEditModal(sk, row, challanCols, "History Of Challan")} sectionKey="challan" onBulkDelete={(ids) => deleteBulk("challan", ids)} />
                </div>

                <div className="sv-section">
                  <div className="sv-section-header">
                    <div className="sv-section-title">History Of Hypothecation</div>
                  </div>
                  <DynamicTable columns={hypoCols} rows={hypothecationRows} onDelete={(id) => deleteRow("hypothecation", id)} onEdit={(sk, row) => openEditModal(sk, row, hypoCols, "History Of Hypothecation")} sectionKey="hypothecation" onBulkDelete={(ids) => deleteBulk("hypothecation", ids)} />
                </div>

                <div className="sv-section">
                  <div className="sv-section-header">
                    <div className="sv-section-title">History Of Insurance</div>
                  </div>
                  <DynamicTable columns={insCols} rows={data.insurance_history} onDelete={(id) => deleteRow("insurance", id)} onEdit={(sk, row) => openEditModal(sk, row, insCols, "History Of Insurance")} sectionKey="insurance" onBulkDelete={(ids) => deleteBulk("insurance", ids)} />
                </div>

                <div className="sv-section">
                  <div className="sv-section-header">
                    <div className="sv-section-title">History Of Paid Fees</div>
                  </div>
                  <DynamicTable columns={paidFeesCols} rows={data.paid_fees_history} onDelete={(id) => deleteRow("paid_fees", id)} onEdit={(sk, row) => openEditModal(sk, row, paidFeesCols, "History Of Paid Fees")} sectionKey="paid_fees" onBulkDelete={(ids) => deleteBulk("paid_fees", ids)} />
                </div>

                <div className="sv-section">
                  <div className="sv-section-header">
                    <div className="sv-section-title">History Of Road Tax</div>
                  </div>
                  <DynamicTable columns={roadTaxCols} rows={data.road_tax_history} onDelete={(id) => deleteRow("road_tax", id)} onEdit={(sk, row) => openEditModal(sk, row, roadTaxCols, "History Of Road Tax")} sectionKey="road_tax" onBulkDelete={(ids) => deleteBulk("road_tax", ids)} />
                </div>

                <div className="sv-section">
                  <div className="sv-section-header">
                    <div className="sv-section-title">History Of Ownership</div>
                  </div>
                  <DynamicTable columns={ownershipCols} rows={data.ownership_history} onDelete={(id) => deleteRow("ownership", id)} onEdit={(sk, row) => openEditModal(sk, row, ownershipCols, "History Of Ownership")} sectionKey="ownership" onBulkDelete={(ids) => deleteBulk("ownership", ids)} />
                </div>

                <div className="sv-section">
                  <div className="sv-section-header">
                    <div className="sv-section-title">History Of Blacklist</div>
                  </div>
                  <DynamicTable columns={blacklistCols} rows={data.blacklist_history} onDelete={(id) => deleteRow("blacklist", id)} onEdit={(sk, row) => openEditModal(sk, row, blacklistCols, "History Of Blacklist")} sectionKey="blacklist" onBulkDelete={(ids) => deleteBulk("blacklist", ids)} />
                </div>

                <div className="sv-section">
                  <div className="sv-section-header">
                    <div className="sv-section-title">History Of Permit</div>
                  </div>
                  <DynamicTable columns={permitCols} rows={data.permit_history} onDelete={(id) => deleteRow("permit", id)} onEdit={(sk, row) => openEditModal(sk, row, permitCols, "History Of Permit")} sectionKey="permit" onBulkDelete={(ids) => deleteBulk("permit", ids)} />
                </div>

                <div className="sv-section">
                  <div className="sv-section-header">
                    <div className="sv-section-title">History Of Tax Clear</div>
                  </div>
                  <DynamicTable columns={taxClearCols} rows={data.tax_clear_history} onDelete={(id) => deleteRow("tax_clear", id)} onEdit={(sk, row) => openEditModal(sk, row, taxClearCols, "History Of Tax Clear")} sectionKey="tax_clear" onBulkDelete={(ids) => deleteBulk("tax_clear", ids)} />
                </div>

                <div className="sv-section">
                  <div className="sv-section-header">
                    <div className="sv-section-title">History Of Fitness</div>
                  </div>
                  <DynamicTable columns={fitnessCols} rows={fitnessHistoryDisplay} onDelete={(id) => deleteRow("fitness", id)} onEdit={(sk, row) => openEditModal(sk, row, fitnessCols, "History Of Fitness")} sectionKey="fitness" onBulkDelete={(ids) => deleteBulk("fitness", ids)} />
                </div>

                <div className="sv-section">
                  <div className="sv-section-header">
                    <div className="sv-section-title">History Of Changed Vehicle Record By Admin</div>
                  </div>
                  <DynamicTable columns={changedCols} rows={data.changed_vehicle_history_by_admins} onDelete={(id) => deleteRow("changed_vehicle", id)} onEdit={(sk, row) => openEditModal(sk, row, changedCols, "History Of Changed Vehicle Record By Admin")} sectionKey="changed_vehicle" onBulkDelete={(ids) => deleteBulk("changed_vehicle", ids)} />
                </div>

                <div className="sv-section">
                  <div className="sv-section-header">
                    <div className="sv-section-title">History Of Rc Print Details</div>
                  </div>
                  <DynamicTable columns={rcCols} rows={data.rc_print_history} onDelete={(id) => deleteRow("rc_print", id)} onEdit={(sk, row) => openEditModal(sk, row, rcCols, "History Of Rc Print Details")} sectionKey="rc_print" onBulkDelete={(ids) => deleteBulk("rc_print", ids)} />
                </div>

                <div className="sv-section">
                  <div className="sv-section-header">
                    <div className="sv-section-title">History Of NOC</div>
                  </div>
                  <DynamicTable columns={nocCols} rows={data.noc_history} onDelete={(id) => deleteRow("noc", id)} onEdit={(sk, row) => openEditModal(sk, row, nocCols, "History Of NOC")} sectionKey="noc" onBulkDelete={(ids) => deleteBulk("noc", ids)} />
                </div>

                <div className="sv-footer">
                  <button className="sv-pdf" onClick={downloadPdf} type="button">
                    PDF DOWNLOAD
                  </button>
                </div>
              </>
            )}
          </div>
        )}

        {errorMsg && (
          <div className="sv-empty">
            <div className="sv-empty-title">No data found</div>
            <div className="sv-empty-sub">{errorMsg}</div>
            <div className="sv-empty-actions">
              {onGoUpload && (
                <button className="sv-btn" type="button" onClick={onGoUpload}>
                  AI Parser
                </button>
              )}
              <button className="sv-btn sv-secondary" type="button" onClick={() => setErrorMsg("")}>
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Edit row modal */}
        {editModal && (
          <div className="sv-save-modal-overlay" onClick={closeEditModal}>
            <div className="sv-edit-modal sv-save-modal" onClick={(e) => e.stopPropagation()}>
              <div className="sv-save-modal-header">
                <h3>Edit row — {editModal.sectionTitle} (ID: {editModal.row?.id})</h3>
                <button type="button" className="sv-save-modal-close" onClick={closeEditModal} aria-label="Close">×</button>
              </div>
              <div className="sv-edit-form">
                {editModal.columns.map((c) => (
                  <div key={c.key} className="sv-edit-field">
                    <label htmlFor={`edit-${c.key}`}>{(c.label || c.key).replace(/_/g, " ")}</label>
                    <input
                      id={`edit-${c.key}`}
                      type="text"
                      value={editForm[c.key] ?? ""}
                      onChange={(e) => setEditForm((prev) => ({ ...prev, [c.key]: e.target.value }))}
                      placeholder={c.label}
                    />
                  </div>
                ))}
              </div>
              <div className="sv-save-modal-actions">
                <button type="button" className="sv-btn sv-secondary" onClick={closeEditModal}>Cancel</button>
                <button type="button" className="sv-btn" onClick={handleSaveEdit} disabled={editModalLoading}>
                  {editModalLoading ? "Saving..." : "Save changes"}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}


